import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  TablePagination,
  TableSortLabel,
  TextField,
  InputAdornment,
  CircularProgress,
  useTheme,
  alpha,
  Autocomplete,
  Grid
} from '@mui/material';
import { Search as SearchIcon, LocationOn as LocationIcon, LocalHospital as HospitalIcon } from '@mui/icons-material';
import useAreaWiseData from '../../../hooks/useAreaWiseData';
import { styled } from '@mui/material/styles';
import defaultInstance from '../../../axiosHelper';
import { API_ENDPOINTS } from '../../../utils/apiEndPoints';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const SearchField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
  },
  marginBottom: theme.spacing(2),
}));

const AreaWiseData = () => {
  const theme = useTheme();

  // State for filters - must be declared before using in useAreaWiseData
  const [districts, setDistricts] = React.useState([]);
  const [healthFacilities, setHealthFacilities] = React.useState([]);
  const [selectedDistrict, setSelectedDistrict] = React.useState(null);
  const [selectedHealthFacility, setSelectedHealthFacility] = React.useState(null);
  const [allData, setAllData] = React.useState([]);

  // Initialize the data hook with filter states
  const {
    data,
    loading,
    error,
    filters,
    handleSort,
    handleSearch,
    handleChangePage,
    handleChangeRowsPerPage,
  } = useAreaWiseData(selectedDistrict, selectedHealthFacility);

  // Fetch all data for filters
  const fetchAllData = async () => {
    try {
      const response = await defaultInstance.get(API_ENDPOINTS.AREA_WISE_DATA, {
        params: { all: true } // Add a parameter to fetch all records
      });
      if (response?.data?.success) {
        const allData = response.data.data || [];
        setAllData(allData);

        // Extract unique districts, filter out 'DISTRICT' and falsy values
        const uniqueDistricts = [...new Set(
          allData
            .map(item => item.district)
            .filter(district =>
              district &&
              typeof district === 'string' &&
              district.toUpperCase() !== 'DISTRICT'
            )
        )];
        setDistricts(uniqueDistricts);

        // Extract unique health facilities
        const uniqueHealthFacilities = [...new Set(
          allData.map(item => item.health_facility).filter(Boolean)
        )];
        setHealthFacilities(uniqueHealthFacilities);
      }
    } catch (error) {
      console.error('Error fetching all data for filters:', error);
    }
  };

  // Update health facilities when district changes
  React.useEffect(() => {
    if (selectedDistrict && allData.length > 0) {
      const filteredData = allData.filter(item => item.district === selectedDistrict);
      const uniqueHealthFacilities = [...new Set(
        filteredData.map(item => item.health_facility).filter(Boolean)
      )];
      setHealthFacilities(uniqueHealthFacilities);
    } else if (allData.length > 0) {
      const uniqueHealthFacilities = [...new Set(
        allData.map(item => item.health_facility).filter(Boolean)
      )];
      setHealthFacilities(uniqueHealthFacilities);
    }
  }, [selectedDistrict, allData]);

  // Initial data fetch
  React.useEffect(() => {
    fetchAllData();
  }, []);

  // Handle district selection
  const handleDistrictChange = (event, newValue) => {
    setSelectedDistrict(newValue);
    setSelectedHealthFacility(null); // Reset health facility when district changes

    // Reset to first page when filters change
    handleChangePage(null, 0);
  };

  // Handle health facility selection
  const handleHealthFacilityChange = (event, newValue) => {
    setSelectedHealthFacility(newValue);

    // Reset to first page when filters change
    handleChangePage(null, 0);
  };

  // Handle search input
  const handleSearchInput = (event) => {
    handleSearch(event);
    handleChangePage(null, 0); // Reset to first page when searching
  };

  const columns = [
    { id: 'srNo', label: 'SL NO.', sortable: false },
    { id: 'district', label: 'DISTRICT' },
    { id: 'health_facility', label: 'HEALTH FACILITY NAME' },
    { id: 'village', label: 'VILLAGE NAME' },
  ];

  return (
    <Box sx={{
      height: '90vh',
      display: 'flex',
      flexDirection: 'column',
      p: { xs: 0, md: 2 },
      boxSizing: 'border-box',
      overflow: 'hidden'
    }}>

         <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', textAlign: 'center' }}>
          LIST OF DICTRICT, HEALTH FACILITY AND VILLAGES WHERE CMHA SCREENING WAS CONDUCTED (MIDORI August 2024 to Feb 2025)
        </Typography>

      <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="flex-end">
          {/* Search Input - Moved to top */}
          <Grid item xs={12} sm={4} sx={{ width: '57.9%' }}>
            <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1, fontWeight: 500 }}>
              Search
            </Typography>
            <Box sx={{ position: 'relative' }}>
              <SearchIcon
                sx={{
                  position: 'absolute',
                  left: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'text.secondary',
                  zIndex: 1,
                }}
              />
              <input
                type="text"
                value={filters.searchTerm}
                onChange={handleSearchInput}
                placeholder="Search by district, health facility, or village..."
                style={{
                  width: '100%',
                  padding: '8px 16px 8px 40px',
                  fontSize: '14px',
                  borderRadius: '4px',
                  border: '1px solid #e0e0e0',
                  backgroundColor: 'white',
                  outline: 'none',
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                  height: '40px',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = theme.palette.primary.main;
                  e.target.style.boxShadow = `0 0 0 2px ${theme.palette.primary.light}40`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e0e0e0';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </Box>
          </Grid>

          {/* District Filter */}
          <Grid item xs={12} sm={4} sx={{ width: '20%' }}>
            <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1, fontWeight: 500 }}>
              District
            </Typography>
            <Autocomplete
              options={districts}
              value={selectedDistrict}
              onChange={handleDistrictChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="medium"
                  placeholder="Select District"
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                    //   minHeight: '48px',
                      '& fieldset': {
                        borderColor: '#e0e0e0',
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                        boxShadow: `0 0 0 2px ${theme.palette.primary.light}40`,
                      },
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    style: {
                      backgroundColor: 'white',
                      borderRadius: '4px',
                    },
                    startAdornment: (
                      <LocationIcon sx={{ color: 'text.secondary', mr: 1.5, ml: 1.5 }} />
                    ),
                  }}
                />
              )}
              clearOnEscape
              clearOnBlur
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  {option}
                </Box>
              )}
            />
          </Grid>

          {/* Health Facility Filter */}
          <Grid item xs={12} sm={4} sx={{ width: '20%' }}>
            <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1, fontWeight: 500 }}>
              Health Facility
            </Typography>
            <Autocomplete
              options={healthFacilities}
              value={selectedHealthFacility}
              onChange={handleHealthFacilityChange}
              disabled={!selectedDistrict}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="medium"
                  placeholder={!selectedDistrict ? 'Select district first' : 'Select Health Facility'}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                    //   minHeight: '48px',
                      '& fieldset': {
                        borderColor: '#e0e0e0',
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                        boxShadow: `0 0 0 2px ${theme.palette.primary.light}40`,
                      },
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    style: {
                      backgroundColor: 'white',
                      borderRadius: '4px',
                    },
                    startAdornment: (
                      <HospitalIcon sx={{ color: 'text.secondary', mr: 1.5, ml: 1.5 }} />
                    ),
                  }}
                />
              )}
              clearOnEscape
              clearOnBlur
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <HospitalIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  {option}
                </Box>
              )}
            />
          </Grid>

        </Grid>
      </Paper>

      <Paper sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        width: '100%',
        mb: 3
      }}>
        <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={5}>
              <CircularProgress />
            </Box>
          ) : (
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      sortDirection={filters.sortField === column.id ? filters.sortOrder : false}
                      sx={{
                        backgroundColor: 'white',
                        color: theme.palette.common.black,
                        fontWeight: 'bold',
                        '& .MuiTableSortLabel-root': {
                          color: 'inherit',
                          '&:hover': {
                            color: alpha(theme.palette.common.black, 0.8),
                          },
                        },
                        '& .MuiTableSortLabel-icon': {
                          color: `${theme.palette.common.black} !important`,
                        },
                      }}
                    >
                      <TableSortLabel
                        active={filters.sortField === column.id}
                        direction={filters.sortField === column.id ? filters.sortOrder : 'asc'}
                        onClick={() => handleSort(column.id)}
                      >
                        {column.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : error ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ color: 'error.main' }}>
                      Error loading data. Please try again later.
                    </TableCell>
                  </TableRow>
                ) : data.length > 0 ? (
                  data.map((row, rowIndex) => {
                    const srNo = (filters.page * filters.rowsPerPage) + rowIndex + 1;
                    return (
                      <StyledTableRow hover key={rowIndex}>
                        <TableCell>{srNo}</TableCell>
                        {columns.slice(1).map((column) => (
                          <TableCell key={column.id}>
                            {row[column.id] || 'N/A'}
                          </TableCell>
                        ))}
                      </StyledTableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No data available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>
        <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filters.totalItems}
            rowsPerPage={filters.rowsPerPage}
            page={filters.page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              '& .MuiTablePagination-toolbar': {
                flexWrap: 'wrap',
                justifyContent: 'center',
                '& > *': {
                  margin: '4px 8px',
                },
              },
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default AreaWiseData;
